import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useSite } from "@/lib/site";
import { useTheme } from "@/lib/theme";
import AnnouncementBanner from "./AnnouncementBanner";

export default function Navbar({ onBookCall }) {
  const { site } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const loc = useLocation();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 8);
          ticking = false;
        });
        ticking = true;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const links = (site?.nav_links || []).filter((l) => l.visible !== false);
  const brandName = site?.brand?.name || "Miaki";

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-background/70 border-b border-border" : "bg-transparent"
      }`}
    >
      <AnnouncementBanner />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          {site?.brand?.logo ? (
            <div>
              {site.brand.logo_dark ? (
                <>
                  <img src={site.brand.logo_dark} alt="Logo" width="128" height="32" className="h-8 w-auto object-contain dark:hidden block" loading="eager" decoding="async" />
                  <img src={site.brand.logo} alt="Logo" width="128" height="32" className="h-8 w-auto object-contain hidden dark:block" loading="eager" decoding="async" />
                </>
              ) : (
                <img src={site.brand.logo} alt="Logo" width="128" height="32" className="h-8 w-auto object-contain" loading="eager" decoding="async" />
              )}
            </div>
          ) : (
            <div>
              <img src={`${process.env.PUBLIC_URL}/images/miaki_dark.png`} alt={brandName} width="128" height="32" className="h-8 w-auto object-contain dark:hidden block" loading="eager" decoding="async" />
              <img src={`${process.env.PUBLIC_URL}/images/miaki_white.png`} alt={brandName} width="128" height="32" className="h-8 w-auto object-contain hidden dark:block" loading="eager" decoding="async" />
            </div>
          )}
        </Link>

        <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `px-2.5 py-2 text-[13px] font-medium tracking-tight transition hover:text-primary ${
                  isActive ? "text-primary" : "text-foreground/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-9 h-9 grid place-items-center border border-border hover:border-primary transition"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            data-testid="nav-book-call-btn"
            onClick={onBookCall}
            className="hidden md:inline-flex btn-cta bg-primary hover:bg-primary/90 text-white px-4 py-2 text-sm font-semibold tracking-tight transition"
          >
            Book Strategy Call
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-9 h-9 grid place-items-center border border-border"
            onClick={() => setOpen((o) => !o)}
            aria-label="Open menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <nav aria-label="Mobile navigation" data-testid="mobile-menu" className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-4 grid gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
                className={({ isActive }) =>
                  `py-2.5 text-base font-medium ${isActive ? "text-primary" : "text-foreground/85"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button data-testid="mobile-book-call-btn" onClick={onBookCall} aria-label="Book a Strategy Call with Miaki" className="mt-2 bg-primary text-white py-2.5 text-sm font-semibold">
              Book Strategy Call
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
